import { useCallback } from 'react'
import { useSearchParams } from 'react-router'

import type { SettlementStatusRoot } from '@repo/sdk'

const VALID_STATUSES: ReadonlySet<string> = new Set<SettlementStatusRoot>([
  'PENDING',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
  'BLOCKED',
])

function isSettlementStatus(value: string): value is SettlementStatusRoot {
  return VALID_STATUSES.has(value)
}

function parseStatus(value: string | null): SettlementStatusRoot | '' {
  if (value && isSettlementStatus(value)) {
    return value
  }
  return ''
}

function parsePage(value: string | null): number {
  const num = Number(value)
  return Number.isInteger(num) && num >= 1 ? num : 1
}

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

function parseDate(value: string | null): string {
  if (value && DATE_PATTERN.test(value)) return value
  return ''
}

function buildParams(
  status: SettlementStatusRoot | '',
  dateFrom: string,
  dateTo: string,
  page: number,
): URLSearchParams {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (dateFrom) params.set('dateFrom', dateFrom)
  if (dateTo) params.set('dateTo', dateTo)
  if (page > 1) params.set('page', String(page))
  return params
}

/**
 * The query string is the single source of truth for the 정산 내역 filters — see
 * `useOrderListUrlSync` for why mirroring component state back into the URL was wrong.
 */
export function useSettlementHistoryUrlSync() {
  const [searchParams, setSearchParams] = useSearchParams()

  const status = parseStatus(searchParams.get('status'))
  const dateFrom = parseDate(searchParams.get('dateFrom'))
  const dateTo = parseDate(searchParams.get('dateTo'))
  const currentPage = parsePage(searchParams.get('page'))

  // Changing a filter always starts back on page 1.
  const setStatus = useCallback(
    (next: SettlementStatusRoot | '') => {
      setSearchParams(buildParams(next, dateFrom, dateTo, 1))
    },
    [setSearchParams, dateFrom, dateTo],
  )

  const setDateRange = useCallback(
    (nextFrom: string, nextTo: string) => {
      setSearchParams(buildParams(status, nextFrom, nextTo, 1))
    },
    [setSearchParams, status],
  )

  const resetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams())
  }, [setSearchParams])

  const setPage = useCallback(
    (page: number) => {
      setSearchParams(buildParams(status, dateFrom, dateTo, page))
    },
    [setSearchParams, status, dateFrom, dateTo],
  )

  return {
    status,
    dateFrom,
    dateTo,
    currentPage,
    setStatus,
    setDateRange,
    setPage,
    resetFilters,
  } as const
}
